import { useEffect, useState } from 'react';
import { FormApi, FormStateBasic } from 'informed';

import {
  globalScrollToErrorField,
  publicRegistrationFormGlobalFieldClassName
} from '@nw/shared/utils';

import { OTP_VERIFICATION_ERROR } from '../../constants/FormConstant';
import { AllFormFieldModelsType } from '../../types/FieldModelTypes';
import {
  InlineOtpVerificationError,
  PublicFormMachineStateUnion
} from '../../types/MultiStepRegistrationFormTypes';
import {
  getInlineOtpFieldName,
  getInlineOtpPhoneFieldName,
  getOtpVerificationErrorField,
  isInlineOtpVerified,
  isInlineOtpVerificationPending
} from '../../utils/MultiStepFieldUtils';

interface UseInlineOtpVerificationParams {
  formApi: FormApi;
  stepFields: AllFormFieldModelsType[];
  formValues: FormStateBasic['values'];
  publicFormMachineState: PublicFormMachineStateUnion;
  designSystemType?: string;
}

export const useInlineOtpVerification = ({
  formApi,
  stepFields,
  formValues,
  publicFormMachineState,
  designSystemType
}: UseInlineOtpVerificationParams) => {
  const [inlineOtpVerificationError, setInlineOtpVerificationError] =
    useState<InlineOtpVerificationError | null>(null);

  const fieldNamesParams = {
    stepFields,
    formValues,
    publicFormMachineState
  };

  const showInlineOtpVerificationError = (): void => {
    const errorField = getOtpVerificationErrorField({
      ...fieldNamesParams,
      isOtpSentToUser: publicFormMachineState.context.isOtpSentToUser
    });

    if (!errorField) {
      return;
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const otpFieldName = getInlineOtpFieldName(stepFields);

    if (otpFieldName) {
      formApi.setError(otpFieldName, undefined);
    }

    setInlineOtpVerificationError({
      field: errorField,
      message: OTP_VERIFICATION_ERROR
    });
    formApi.setError(errorField, OTP_VERIFICATION_ERROR);
    formApi.setTouched(errorField, true);

    setTimeout(() => {
      globalScrollToErrorField(publicRegistrationFormGlobalFieldClassName);
    });
  };

  const clearInlineOtpVerificationError = (): void => {
    const phoneFieldName = getInlineOtpPhoneFieldName(fieldNamesParams);
    const otpFieldName = getInlineOtpFieldName(stepFields);
    const fieldsToClear = new Set(
      [inlineOtpVerificationError?.field, phoneFieldName, otpFieldName].filter(
        Boolean
      ) as string[]
    );

    fieldsToClear.forEach((field) => {
      if (formApi.getError(field) === OTP_VERIFICATION_ERROR) {
        formApi.setError(field, undefined);
      }
    });

    if (inlineOtpVerificationError) {
      setInlineOtpVerificationError(null);
    }
  };

  const checkIsInlineOtpVerified = (): boolean =>
    isInlineOtpVerified(publicFormMachineState, stepFields, formValues);

  const checkIsInlineOtpVerificationPending = (): boolean =>
    isInlineOtpVerificationPending(
      publicFormMachineState,
      stepFields,
      formValues
    );

  useEffect(() => {
    if (designSystemType !== 'SHADCN_DESIGN_SYSTEM') {
      return;
    }

    if (checkIsInlineOtpVerified()) {
      clearInlineOtpVerificationError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    designSystemType,
    publicFormMachineState.context.isOtpVerified,
    publicFormMachineState.context.isLoggedIn,
    (
      publicFormMachineState.context.authMachineRef as {
        state?: { context?: { isOtpVerified?: boolean } };
      } | null
    )?.state?.context?.isOtpVerified
  ]);

  return {
    inlineOtpVerificationError,
    showInlineOtpVerificationError,
    clearInlineOtpVerificationError,
    isInlineOtpVerified: checkIsInlineOtpVerified,
    isInlineOtpVerificationPending: checkIsInlineOtpVerificationPending
  };
};
